import { Avatar, Box, Skeleton, SxProps, Typography } from '@mui/material';
import React, { useState } from 'react';
import { addAlphaToColor } from 'shared/utils/colors';

import { EquinetTeams, TeamFilledIcon } from '@/assets/svg';
import AddToTeamIcon from '@/assets/svg/AddToTeamIcon';
import PremiumStarIcon from '@/assets/svg/PremiumStarIcon';
import TeamIcon from '@/assets/svg/TeamIcon';
import { Button } from '@/components';
import { useIsPremiumFeatureAvailable } from '@/hooks';
import { useCanManageSubscription } from '@/hooks/useCanManageSubscription';
import { useReachedTeamSeatsLimit } from '@/hooks/useReachedTeamSeatsLimit';
import { UnlockPremiumModal } from '@/pages/PremiumPage/components';
import { t } from '@/services/translations/config';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';

import { Props } from './types';

const buttonsSx: SxProps = {
    color: COLOR.white,
    backgroundColor: ENTITY_COLOR.teams,
    borderColor: COLOR.white,
    borderRadius: '3px',
    height: '28px',
    fontWeight: 'normal',
};

function Header({
    isTeamActive,
    teamPhotoUrl,
    teamName,
    onInviteNewMember,
    onEditTeam,
    isLoading,
    isYourTeam = false,
}: Props) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { isPremiumFeatureAvailable } = useIsPremiumFeatureAvailable();

    const { seatsLimit, isPremiumEnabled, takenSeats } =
        useReachedTeamSeatsLimit();
    const isOrganisationOwner = useCanManageSubscription();

    const openPremiumModal = () => {
        if (!isPremiumEnabled) {
            return;
        }

        setIsModalOpen(true);
    };

    const closePremiumModal = () => {
        setIsModalOpen(false);
    };

    const handleInviteNewMember = () => {
        if (!isPremiumFeatureAvailable) {
            openPremiumModal();
            return;
        }

        onInviteNewMember?.();
    };

    return (
        <>
            {isModalOpen && isOrganisationOwner && (
                <UnlockPremiumModal
                    title={t('TeamsPage:addModal:title')}
                    description={t('TeamsPage:addModal:description')}
                    logo={
                        <Box className="flex gap-2 items-center">
                            <EquinetTeams
                                width={210}
                                color={ENTITY_COLOR.teams}
                            />
                        </Box>
                    }
                    color={ENTITY_COLOR.teams}
                    close={closePremiumModal}
                />
            )}

            <Box
                sx={{
                    alignItems: 'center',
                    background:
                        'transparent linear-gradient(0deg, #5D5288 0%, #554F6C 100%) 0% 0% no-repeat padding-box',
                    display: 'flex',
                    height: '180px',
                }}
            >
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'row',
                        mx: 'auto',
                        pl: '50px',
                        pr: '80px',
                        maxWidth: '1300px',
                        width: '100%',
                    }}
                >
                    {isLoading ? (
                        <Skeleton
                            data-test-id={'TeamsHeader-Skeleton'}
                            width="70%"
                            height={76}
                            variant="text"
                        />
                    ) : (
                        <>
                            <Box
                                sx={{
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {!isTeamActive ? (
                                    <>
                                        <TeamIcon height={60} width={86} />
                                        <Typography
                                            sx={{
                                                backgroundColor:
                                                    COLOR.whiteDarker2,
                                                borderRadius: 30,
                                                textAlign: 'center',
                                                width: '135px',
                                                height: '21px',
                                                color: ENTITY_COLOR.teams,
                                                fontSize: 14,
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {t('TeamsPage:equinet_teams', {
                                                defaultValue: 'EQUINET Teams',
                                            })}
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Box
                                            sx={{
                                                width: '126px',
                                                height: '126px',
                                                display: 'flex',
                                                borderRadius: '10px',
                                                position: 'relative',
                                                overflow: 'visible',
                                                background: 'white',
                                                boxShadow:
                                                    '0px 3px 6px #00000029',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                mb: -12,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: '120px',
                                                    height: '120px',
                                                    display: 'flex',
                                                    borderRadius: '10px',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    backgroundColor: 'white',
                                                }}
                                            >
                                                <Avatar
                                                    src={
                                                        teamPhotoUrl ||
                                                        undefined
                                                    }
                                                    sx={{
                                                        cursor: 'inherit',
                                                        width: '120px',
                                                        height: '120px',
                                                        backgroundColor:
                                                            COLOR.eclipse,
                                                    }}
                                                    variant="square"
                                                >
                                                    <TeamIcon
                                                        height={80}
                                                        width={80}
                                                        color={'grey'}
                                                    />
                                                </Avatar>
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                            {!isTeamActive ? (
                                <Typography
                                    sx={{
                                        width: '204px',
                                        textAlign: 'center',
                                        color: COLOR.whiteDarker2,
                                        ml: '21px',
                                    }}
                                >
                                    {t('TeamsPage:collaborate', {
                                        defaultValue:
                                            'Collaborate with members in your organisation',
                                    })}
                                </Typography>
                            ) : (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        ml: '15px',
                                        pt: 6,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                sx={{
                                                    color: COLOR.white,
                                                    fontSize: 20,
                                                }}
                                            >
                                                {teamName}
                                            </Typography>
                                            {isYourTeam ? (
                                                <Typography
                                                    sx={{
                                                        color: COLOR.white,
                                                        opacity: '42%',
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {t('TeamsPage:your_team')}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <TeamFilledIcon
                                                height={22}
                                                width={36}
                                                color={COLOR.white}
                                            />
                                            <Typography
                                                sx={{
                                                    color: COLOR.white,
                                                    fontSize: 12,
                                                    fontWeight: 'bold',
                                                    mt: '2px',
                                                }}
                                            >
                                                {t('Static:active')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                mb: 1,
                                                mt: 3,
                                                display: 'flex',
                                                gap: 2,
                                            }}
                                        >
                                            {onInviteNewMember ? (
                                                <Button
                                                    variant={'outlined'}
                                                    sx={buttonsSx}
                                                    startIcon={
                                                        <AddToTeamIcon />
                                                    }
                                                    caption={t(
                                                        'Organisation:invite_new_members',
                                                    )}
                                                    disabled={Boolean(
                                                        !isOrganisationOwner,
                                                    )}
                                                    onClick={
                                                        handleInviteNewMember
                                                    }
                                                />
                                            ) : null}
                                            {onEditTeam ? (
                                                <Button
                                                    variant={'outlined'}
                                                    sx={buttonsSx}
                                                    caption={t(
                                                        'CreateTeam:edit_team',
                                                    )}
                                                    onClick={onEditTeam}
                                                />
                                            ) : null}
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,

                                                backgroundColor:
                                                    addAlphaToColor(
                                                        COLOR.black,
                                                        0.15,
                                                    ),
                                                borderRadius: 20,
                                                px: '8px',
                                                py: '4px',
                                                mt: !isOrganisationOwner
                                                    ? '24px'
                                                    : '0px',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                }}
                                            >
                                                {isPremiumEnabled && (
                                                    <Box
                                                        sx={{
                                                            backgroundColor:
                                                                COLOR.white,
                                                            borderRadius:
                                                                '50px',
                                                        }}
                                                    >
                                                        <PremiumStarIcon
                                                            width={14}
                                                            height={14}
                                                            color={
                                                                ENTITY_COLOR.teams
                                                            }
                                                            secondaryColor={
                                                                COLOR.white
                                                            }
                                                        />
                                                    </Box>
                                                )}
                                                {seatsLimit ? (
                                                    <Typography
                                                        color={COLOR.white}
                                                        fontSize={12}
                                                    >
                                                        {`${takenSeats}/${seatsLimit}`}{' '}
                                                        {t(
                                                            'TeamsPage:addModal:team_seats_in_use',
                                                        )}
                                                    </Typography>
                                                ) : null}
                                            </Box>

                                            {isPremiumEnabled &&
                                                isOrganisationOwner && (
                                                    <Button
                                                        sx={{
                                                            backgroundColor:
                                                                COLOR.mountainMajesty,
                                                            color: COLOR.white,
                                                            fontSize: 12,
                                                            fontWeight: 700,
                                                            height: 22,
                                                            borderRadius: 20,
                                                            px: '10px',
                                                        }}
                                                        onClick={
                                                            openPremiumModal
                                                        }
                                                        caption={t(
                                                            'TeamsPage:addModal:add_remove',
                                                        )}
                                                    />
                                                )}
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </>
    );
}

export default Header;
